// Van "er zijn nieuwe events" naar "wat laten we zien en horen". Pure logica, los
// van React en van de browser, zodat de regels tegen meldingsmoeheid te testen zijn.
//
// Drie niveaus: 1 = gebeurtenis (kleine toast), 2 = prestatie (toast met accent),
// 3 = mijlpaal (mijlpaalkaart). Per ophaalronde klinkt hoogstens ÉÉN sound: die van
// het hoogste niveau.

// ── Persoonlijke voorkeuren (user_ui_prefs, sleutels gami.*) ─────────────────
export const STANDAARD_GELUID = { aan: true, volume: null };          // null = volg de standaard van de beheerder
export const STANDAARD_KANALEN = { toasts: true, browser: false, mail: true, digest: true, trillen: false, categorieen: {}, pauzeTot: null };

export const leesGeluid = (v) => ({ ...STANDAARD_GELUID, ...(v && typeof v === 'object' ? v : {}) });
export const leesKanalen = (v) => {
  const k = { ...STANDAARD_KANALEN, ...(v && typeof v === 'object' ? v : {}) };
  k.categorieen = { ...(k.categorieen && typeof k.categorieen === 'object' ? k.categorieen : {}) };
  return k;
};

// "Pauzeer alles voor vandaag": pauzeTot = de dag (YYYY-MM-DD) tot en met wanneer.
export const vandaagIso = (nu = new Date()) => `${nu.getFullYear()}-${String(nu.getMonth() + 1).padStart(2, '0')}-${String(nu.getDate()).padStart(2, '0')}`;
export const isGepauzeerd = (kanalen, nu = new Date()) => Boolean(kanalen && kanalen.pauzeTot && kanalen.pauzeTot >= vandaagIso(nu));

export const wilCategorie = (kanalen, categorie) => !(kanalen && kanalen.categorieen && kanalen.categorieen[categorie] === false);

// Effectief volume: 0 als geluid nergens mag, anders de eigen keuze of de standaard.
export function effectiefVolume(mij, geluid) {
  if (!mij || !mij.actief || !mij.soundsBeschikbaar) return 0;
  const g = leesGeluid(geluid);
  if (!g.aan) return 0;
  const v = g.volume == null ? mij.standaardVolume : g.volume;
  return Math.min(100, Math.max(0, Number(v) || 0));
}

const nl = (n, dec = 0) => new Intl.NumberFormat('nl-BE', { maximumFractionDigits: dec }).format(Number(n) || 0);
export function volumeTekst(kwh) {
  const v = Number(kwh) || 0;
  if (v >= 1e6) return `${nl(v / 1e6, v >= 1e7 ? 1 : 2)} GWh`;
  if (v >= 1000) return `${nl(v / 1000, v >= 100000 ? 0 : 1)} MWh`;
  return `${nl(v)} kWh`;
}

const ORDER_KEYS = new Set(['sale.new', 'customer.new', 'connection.new']);
const SOUND_RANG = { geen: 0, sale: 1, klant: 2, reward: 3, prestatie: 4, mijlpaal: 5 };

// events = nieuwe events uit één ophaalronde (nieuwste eerst of niet, maakt niet
// uit). Geeft { toasts, kaart, sound, browser } terug:
//   toasts  [{ titel, tekst, tier, ids }]
//   kaart   { titel, tekst, ids } | null     (één mijlpaalkaart per ronde)
//   sound   naam | null
//   browser [{ titel, tekst }]               (voor een verborgen tabblad)
export function bundel(events, { kanalen, nu = new Date() } = {}) {
  const k = leesKanalen(kanalen);
  const leeg = { toasts: [], kaart: null, sound: null, browser: [] };
  if (isGepauzeerd(k, nu)) return leeg;
  const live = (events || []).filter((e) => e && e.live && wilCategorie(k, e.categorie));
  if (!live.length) return leeg;

  const toasts = [];
  // Gewone orders: één toast, hoeveel het er ook zijn.
  const orders = live.filter((e) => e.tier === 1 && ORDER_KEYS.has(e.key));
  if (orders.length === 1) {
    toasts.push({ titel: orders[0].titel, tekst: orders[0].tekst, tier: 1, ids: [orders[0].id] });
  } else if (orders.length > 1) {
    const kwh = orders.reduce((s, e) => s + (Number(e.kwh) || 0), 0);
    const klanten = orders.filter((e) => e.key === 'customer.new').length;
    const delen = [kwh > 0 ? `${volumeTekst(kwh)}/jaar` : '', klanten === 1 ? '1 nieuwe klant' : klanten > 1 ? `${klanten} nieuwe klanten` : ''].filter(Boolean);
    toasts.push({ titel: `${orders.length} nieuwe sales`, tekst: delen.join(' · '), tier: 1, ids: orders.map((e) => e.id) });
  }
  const rest = live.filter((e) => !orders.includes(e)).sort((a, b) => b.tier - a.tier || b.id - a.id);
  let kaart = null;
  for (const e of rest) {
    if (e.tier >= 3 && !kaart) kaart = { titel: e.titel, tekst: e.tekst, key: e.key, ids: [e.id] };
    else toasts.push({ titel: e.titel, tekst: e.tekst, tier: e.tier, ids: [e.id] });
  }

  // Eén sound: die van het hoogste niveau; bij gelijk niveau de "rijkste".
  const metSound = live.filter((e) => e.sound && e.sound !== 'geen')
    .sort((a, b) => b.tier - a.tier || (SOUND_RANG[b.sound] || 0) - (SOUND_RANG[a.sound] || 0));
  const sound = metSound.length ? metSound[0].sound : null;

  const browser = live.filter((e) => e.browser).sort((a, b) => b.tier - a.tier).slice(0, 2).map((e) => ({ titel: e.titel, tekst: e.tekst }));
  return { toasts: k.toasts ? toasts : [], kaart: k.toasts ? kaart : null, sound, browser };
}

// Plafond: hoogstens `max` binnen het venster. `tijden` = eerdere tijdstippen (ms).
// Geeft { mag, tijden } terug; de aanroeper bewaart de nieuwe lijst.
export function plafond(tijden, max, vensterMs, nu = Date.now()) {
  const recent = (tijden || []).filter((t) => nu - t < vensterMs);
  if (recent.length >= max) return { mag: false, tijden: recent };
  return { mag: true, tijden: [...recent, nu] };
}

// "Sinds je laatste bezoek": samenvatting van wat er ongelezen staat.
export function sindsLaatsteBezoek(items, lastSeenId) {
  const nieuw = (items || []).filter((e) => e.id > (lastSeenId || 0));
  if (!nieuw.length) return null;
  const orders = nieuw.filter((e) => ORDER_KEYS.has(e.key));
  const klanten = orders.filter((e) => e.key === 'customer.new').length;
  const mijlpalen = nieuw.filter((e) => e.tier >= 3).length;
  const prestaties = nieuw.filter((e) => e.tier === 2).length;
  const delen = [];
  if (orders.length) delen.push(orders.length === 1 ? '1 nieuwe sale' : `${orders.length} nieuwe sales`);
  if (klanten) delen.push(klanten === 1 ? '1 nieuwe klant' : `${klanten} nieuwe klanten`);
  if (prestaties) delen.push(prestaties === 1 ? '1 prestatie' : `${prestaties} prestaties`);
  if (mijlpalen) delen.push(mijlpalen === 1 ? '1 mijlpaal' : `${mijlpalen} mijlpalen`);
  return { aantal: nieuw.length, tekst: delen.join(', ') };
}

// Groepeert de lijst per dag voor het paneel Hoogtepunten.
export function perDag(items) {
  const groepen = [];
  for (const e of items || []) {
    const d = new Date(e.at);
    const dag = Number.isNaN(d.getTime()) ? '' : vandaagIso(d);
    const laatste = groepen[groepen.length - 1];
    if (laatste && laatste.dag === dag) laatste.items.push(e);
    else groepen.push({ dag, items: [e] });
  }
  return groepen;
}
